import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { hubMessage } from '../../chat/entities/chatHub-message';

// Connection Status Interface
interface ConnectionStatus {
  isConnected: boolean;
  connectionId: string | null;
  lastConnectedAt: string | null;
  error: string | null;
}

// SignalR State Interface
interface SignalRState {
  connectionStatus: ConnectionStatus;
  isInitialized: boolean;
  lastMessage: hubMessage | null;
  messageQueue: hubMessage[];
  notifications: any[];
  unreadMessageCount: number;
}

// Initial State
const initialState: SignalRState = {
  connectionStatus: {
    isConnected: false,
    connectionId: null,
    lastConnectedAt: null,
    error: null
  },
  isInitialized: false,
  lastMessage: null,
  messageQueue: [],
  notifications: [],
  unreadMessageCount: 0
};

// SignalR Slice
const signalRSlice = createSlice({
  name: 'signalr',
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connectionStatus = action.payload;
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },
    addMessage: (state, action: PayloadAction<hubMessage>) => {
      state.lastMessage = action.payload;
      state.messageQueue.push(action.payload);
      // Keep only last 50 messages in queue
      if (state.messageQueue.length > 50) {
        state.messageQueue = state.messageQueue.slice(-50);
      }
    },
    incrementUnreadMessageCount: (state) => {
      state.unreadMessageCount += 1;
    },
    resetUnreadMessageCount: (state) => {
      state.unreadMessageCount = 0;
    },
    setUnreadMessageCount: (state, action: PayloadAction<number>) => {
      state.unreadMessageCount = action.payload;
    },
    addNotification: (state, action: PayloadAction<any>) => {
      state.notifications.push(action.payload);
      // Keep only last 20 notifications
      if (state.notifications.length > 20) {
        state.notifications = state.notifications.slice(-20);
      }
    },
    clearMessageQueue: (state) => {
      state.messageQueue = [];
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    reset: (state) => {
      state.connectionStatus = initialState.connectionStatus;
      state.isInitialized = false;
      state.lastMessage = null;
      state.messageQueue = [];
      state.notifications = [];
      state.unreadMessageCount = 0;
    }
  }
});

export const {
  setConnectionStatus,
  setInitialized,
  addMessage,
  addNotification,
  clearMessageQueue,
  clearNotifications,
  reset,
  incrementUnreadMessageCount,
  resetUnreadMessageCount,
  setUnreadMessageCount
} = signalRSlice.actions;

export default signalRSlice.reducer; 